import React, { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { Link } from 'react-router-dom';
import {
  PieChart, User,
  Settings,
  ChevronLeft, ChevronRight, LogOut, Activity, Calendar, CalendarClock, Users, MessageCircle
} from 'lucide-react';
import Logo from './Logo';

function Sidebar({
  isCollapsed, 
  setIsCollapsed,
  isMobileMenuOpen, 
  setIsMobileMenuOpen
}) {
  // Dati Auth
  const { user, logout: logoutUser } = useContext(AuthContext);
  const { admin, logout: logoutAdmin } = useContext(AdminAuthContext);

  // Funzioni
  const isAdmin = !!admin;
  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const logout = () => {
    if (admin) {
      logoutAdmin();
    } else {
      logoutUser();
    }
  };

  // Config contesti
  const contextConfigs = {
    admin: {
      title: 'Admin Dashboard',
      variant: 'primary', // Usa i colori primari (beige) per admin
      navigationItems: [
        { icon: PieChart, label: 'Dashboard', path: '/admin' },
        { icon: Activity, label: 'Stato Becco', path: '/stato-becco' },
        { icon: CalendarClock, label: 'Eventi', path: '/gestione-eventi' },
        { icon: Users, label: 'Amici', path: '/gestione-amici' },
      ],
    },
    user: {
      title: 'Dashboard Utente',
      variant: 'secondary', // Usa i colori secondari (blu) per user
      navigationItems: [
        { icon: PieChart, label: 'Dashboard', path: '/dashboard' },
        { icon: Calendar, label: 'Eventi', path: '/eventi' },
      ],
    },
  };

  const config = isAdmin ? contextConfigs.admin : contextConfigs.user;

  // Theme classes basate sulla variante
  const themeClasses = {
    primary: {
      hover: 'hover:bg-light-primary dark:hover:bg-dark-primary',
      icon: 'text-light-primary dark:text-dark-primary',
      text: 'text-light-on-primary dark:text-dark-on-primary',
      bg: 'bg-light-primary dark:bg-dark-primary',
      groupHover: 'group-hover:text-light-on-primary dark:group-hover:text-dark-on-primary'
    },
    secondary: {
      hover: 'hover:bg-light-secondary dark:hover:bg-dark-secondary',
      icon: 'text-light-secondary dark:text-dark-secondary',
      text: 'text-light-on-secondary dark:text-dark-on-secondary',
      bg: 'bg-light-secondary dark:bg-dark-secondary',
      groupHover: 'group-hover:text-light-on-secondary dark:group-hover:text-dark-on-secondary'
    }
  };

  const classes = themeClasses[config.variant];

  // Componente interno per i link
  const NavItem = ({ icon: Icon, label, path }) => (
    <Link 
      to={path} 
      className="block relative w-full py-2"
      onClick={() => {
        // Chiudi il menu mobile quando si clicca su un link
        if (window.innerWidth < 1024) { // 1024px è il breakpoint 'lg' di Tailwind
          setIsMobileMenuOpen(false);
        }
      }}
    >
      <div className={`
        flex items-center
        ${!isCollapsed 
          ? `${classes.hover} rounded-lg px-3`
          : 'justify-center'
        }
        group relative
      `}>
        <div className={`
          relative flex items-center justify-center
          w-10 h-10
          ${isCollapsed &&
            `${classes.hover} rounded-full`
          }
          transition-colors duration-300
        `}>
          <Icon
            size={20}
            className={`text-light-on-surface dark:text-dark-on-surface transition-transform duration-300 ease-in-out ${classes.groupHover} group-hover:scale-110`}
          />
        </div>

        <span className={`
          whitespace-nowrap text-light-on-surface dark:text-dark-on-surface
          ${classes.groupHover}
          overflow-hidden transition-all duration-300 ease-in-out
          ${isCollapsed ? 'ml-0 opacity-0 w-0' : 'ml-3 opacity-100 w-auto'}
        `}>
          {label}
        </span>

        {/* Tooltip */}
        {isCollapsed && (
          <div className={`
            pointer-events-none absolute left-full top-1/2 -translate-y-1/2
            ml-2 px-3 py-2
            ${classes.bg}
            ${classes.text}
            rounded-lg
            opacity-0 invisible translate-x-2
            group-hover:opacity-100 group-hover:visible group-hover:translate-x-0
            transition-all duration-300 ease-in-out
            whitespace-nowrap z-50 text-sm
          `}>
            {label}
          </div>
        )}
      </div>
    </Link>
  );

  return (
    <div className={`
      fixed top-0 left-0
      ${isCollapsed ? 'w-20' : 'w-72'}
      bg-light-surface-container dark:bg-dark-surface-container
      shadow-surface-container
      flex flex-col h-screen px-4
      transition-all duration-300 ease-in-out z-40
      ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
    `}>
      {/* Header unificato responsive */}
      <div className="flex flex-col items-center py-4">
        <div className="hidden lg:flex w-16 h-16 items-center justify-center mb-4">
          <Logo variant={config.variant} size="lg" />
        </div>

        {/* Bottone collapse visibile solo su desktop */}
        <button
          onClick={toggleCollapse}
          className={`
            hidden lg:flex items-center justify-center w-8 h-8
            ${classes.bg}
            ${classes.text}
            rounded-full shadow-surface-container
            hover:opacity-90
            transition-all duration-300 ease-in-out
            focus:outline-none
          `}
        >
          {isCollapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* VOCI DI NAVIGAZIONE */}
      <nav className="flex-1 py-8 lg:py-4">
        {config.navigationItems.map((item, index) => (
          <NavItem key={index} {...item} />
        ))}
      </nav>

      {/* Footer con Settings e Logout */}
      <div className="py-4">
        <NavItem 
          icon={Settings} 
          label="Impostazioni" 
          path={isAdmin ? '/admin/settings' : '/settings'} 
        />

        {(user || admin) ? (
          <button
            onClick={logout}
            className="block relative w-full py-2"
          >
            <div className={`
              flex items-center
              ${!isCollapsed
                ? 'hover:bg-light-error dark:hover:bg-dark-error rounded-lg px-3'
                : 'justify-center'
              }
              group relative
            `}>
              <div className={`
                flex items-center justify-center w-10 h-10
                ${isCollapsed &&
                  'hover:bg-light-error dark:hover:bg-dark-error rounded-full'
                }
                transition-colors duration-300
              `}>
                <LogOut
                  size={20}
                  className="text-light-on-surface dark:text-dark-on-surface transition-transform duration-300 ease-in-out group-hover:text-light-on-error dark:group-hover:text-dark-on-error group-hover:scale-110"
                />
              </div>

              <span
                className={`
                  whitespace-nowrap text-light-on-surface dark:text-dark-on-surface
                  group-hover:text-light-on-error dark:group-hover:text-dark-on-error
                  transition-all duration-300 ease-in-out
                  ${isCollapsed ? 'ml-0 opacity-0 w-0' : 'ml-3 opacity-100'}
                `}
              >
                Logout
              </span>

              {/* Tooltip (collassato) */}
              {isCollapsed && (
                <div
                  className="
                  pointer-events-none
                  absolute left-full top-1/2 -translate-y-1/2
                  ml-2 px-3 py-2
                  bg-light-error dark:bg-dark-error
                  text-light-on-error dark:text-dark-on-error
                  rounded-lg
                  opacity-0 invisible translate-x-2
                  group-hover:opacity-100 group-hover:visible group-hover:translate-x-0
                  transition-all duration-300 ease-in-out
                  whitespace-nowrap
                  z-50
                  text-sm
                "
                >
                  Logout
                </div>
              )}
            </div>
          </button>
        ) : (
          <NavItem icon={User} label="Login" path="/" />
        )}
      </div>
    </div>
  );
}

export default Sidebar;
